import { useState } from "react";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useRecordContext,
  useRefresh,
} from "ra-core";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { UserPlus } from "lucide-react";

import type { Contact } from "../types";

type Sale = { id: number; contact_id: number | null; disabled: boolean };

/**
 * Turns a contact into someone who can log in.
 *
 * The contact record does not change and is not replaced -- its notes, company
 * and deal history stay exactly where they are. What is created is a login that
 * points back at it, which is what makes the client's own projects reachable
 * without adding them to every project by hand.
 *
 * The role is chosen rather than assumed: a collaborator is invited from a
 * contact just as often as a client is.
 */
export const InviteAsClientButton = ({
  role = "client",
  label = "Invite as client",
}: {
  role?: "client" | "collaborator";
  label?: string;
}) => {
  const contact = useRecordContext<Contact>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);

  // Whether this contact already has a login. The unique index means there can
  // only ever be one, so finding it is a lookup rather than a question.
  const { data: existing } = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: 1 },
      filter: { contact_id: contact?.id },
    },
    { enabled: contact?.id != null },
  );

  const alreadyInvited = (existing?.length ?? 0) > 0;

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      if (!contact) throw new Error("No contact");
      const email = contact.email_jsonb?.[0]?.email;
      if (!email) {
        throw new Error(
          "This contact has no email address, so there is nowhere to send the invitation.",
        );
      }

      const sale = await dataProvider.salesCreate({
        email,
        first_name: contact.first_name,
        last_name: contact.last_name,
        administrator: false,
        disabled: false,
        contact_id: contact.id,
      });

      // The role is a join row, so it can only be written once the user exists.
      await dataProvider.create("user_roles", {
        data: { sales_id: sale.id, role_id: role },
      });

      return sale;
    },
    onSuccess: () => {
      setOpen(false);
      notify(
        "Invitation sent. They will receive an email to set their password.",
        { type: "info" },
      );
      refresh();
    },
    onError: (error: Error) => {
      notify(error.message || "Could not send the invitation", {
        type: "error",
      });
    },
  });

  if (!contact) return null;

  if (alreadyInvited) {
    return (
      <Badge variant="secondary" className="gap-1">
        <UserPlus className="size-3" />
        Has an account
      </Badge>
    );
  }

  const email = contact.email_jsonb?.[0]?.email;

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <UserPlus className="size-4" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
            <DialogDescription>
              {email ? (
                <>
                  {contact.first_name} {contact.last_name} will get an email at{" "}
                  <span className="font-medium">{email}</span> to set a
                  password. They will see the projects where they are the
                  client, and the tickets on them — nothing else.
                </>
              ) : (
                <>
                  This contact has no email address, so there is nowhere to send
                  an invitation. Add one first.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => mutate()} disabled={isPending || !email}>
              {isPending ? "Sending…" : "Send invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
